import { formatCalendarDate } from "./format";
import { seasonContains } from "./reservations";
import type { Occupancy, TripContent, TripSeason } from "./types";

/**
 * "Planea tu viaje" (client design, 20 Oct 2026): the trip page asks for a departure, the room and
 * the package instead of listing every season row. A row's `service_category` is the imported
 * label — "Temporada Baja (28 Feb - 23 May / 03 Oct - 21 Nov) · solo terrestre", "Primera · Terrestre
 * Y Aéreo", "Confort" — so each " · " part position across a trip's rows becomes one choice:
 * the season (picked from the departure when its dates can be read), the hotel category, the package.
 */

export type PlanChoiceKind = "season" | "tier" | "option" | "package";

export interface PlanChoice {
  /** Part position in `service_category`; the key of `picks`. */
  index: number;
  kind: PlanChoiceKind;
  label: string;
  /** The values the chosen departure can be priced with (one when there is nothing to choose). */
  options: Array<{ value: string; label: string }>;
  value: string;
  /** The season came from the departure date, so it is shown as a hint instead of a select. */
  auto: boolean;
}

export interface TripPlan {
  /** Choices with more than one option, in display order (season, category, option, package). */
  choices: PlanChoice[];
  /** The `service_category` to price and reserve with; `rateFor` resolves the row. */
  category: string | null;
  season: TripSeason | null;
  /** "Temporada baja · se aplica automáticamente" / "Tarifa vigente 01 nov 2026 – 28 feb 2027". */
  seasonHint: string | null;
  /** "servicios terrestres" when the trip sells packages, for the price line. */
  packageLabel: string | null;
}

const PACKAGE = /terrestre|a[ée]reo/i;
const SEASON = /temporada|salida especial/i;
const MONTHS: Record<string, number> = { ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6, jul: 7, ago: 8, sep: 9, oct: 10, nov: 11, dic: 12 };
const DAY_MONTH = /(\d{1,2})\s+(ene|feb|mar|abr|may|jun|jul|ago|sep|oct|nov|dic)[a-z]*\.?/gi;
const KIND_ORDER: PlanChoiceKind[] = ["season", "tier", "option", "package"];

const partsOf = (season: TripSeason) => (season.service_category || "").split(" · ").map((part) => part.trim());
const sentence = (text: string) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

/** "Solo terrestre" → "Servicios terrestres", "Terrestre Y Aéreo" → "Servicios terrestres y vuelo"; anything else as written. */
export function packageLabel(value: string): string {
  if (/^(solo )?terrestre$/i.test(value)) return "Servicios terrestres";
  if (/^(solo )?terrestre y a[ée]reo$/i.test(value)) return "Servicios terrestres y vuelo";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The yearly windows a season label spells out ("(28 Feb - 23 May / 03 Oct - 21 Nov)", "Salida
 * Especial 26 Dic") as month*100+day pairs; empty when the label carries no dates.
 */
export function seasonWindows(label: string): Array<{ from: number; to: number }> {
  const windows: Array<{ from: number; to: number }> = [];
  for (const chunk of label.split("/")) {
    const days = [...chunk.matchAll(DAY_MONTH)].map((match) => MONTHS[match[2]!.slice(0, 3).toLowerCase()]! * 100 + Number(match[1]));
    if (days.length === 1) windows.push({ from: days[0]!, to: days[0]! });
    else if (days.length === 2) windows.push({ from: days[0]!, to: days[1]! });
  }
  return windows;
}

function inWindows(windows: Array<{ from: number; to: number }>, date: string): boolean {
  const day = Number(date.slice(5, 7)) * 100 + Number(date.slice(8, 10));
  return windows.some(({ from, to }) => (from <= to ? day >= from && day <= to : day >= from || day <= to));
}

/** Hotel category leads the label ("Primera · …"); a later free-form part is an option ("servicio en inglés"). */
function kindOf(values: string[], index: number): PlanChoiceKind {
  if (values.every((value) => PACKAGE.test(value))) return "package";
  if (values.every((value) => SEASON.test(value))) return "season";
  return index === 0 ? "tier" : "option";
}

const LABELS: Record<PlanChoiceKind, string> = { season: "Temporada", tier: "Categoría de hotel", option: "Opción", package: "Tu paquete" };

/**
 * Resolves the season row for a departure and the customer's picks (part index → value). The pick
 * changed last (`lastPicked`) wins when the other picks have no row together with it.
 */
export function planTrip(content: TripContent, departure: string | null, picks: Record<number, string>, lastPicked?: number): TripPlan {
  const rows = content.pricing.seasons;
  if (!rows.length) return { choices: [], category: null, season: null, seasonHint: null, packageLabel: null };
  const parts = rows.map(partsOf);
  const width = Math.max(...parts.map((row) => row.length));

  // The part positions that vary across the trip's rows are its choices.
  const dims: Array<{ index: number; kind: PlanChoiceKind }> = [];
  for (let index = 0; index < width; index += 1) {
    const values = [...new Set(parts.map((row) => row[index]).filter((value): value is string => Boolean(value)))];
    if (values.length < 2) continue;
    dims.push({ index, kind: kindOf(values, index) });
  }

  // Rows that can price the departure; a season whose label dates bracket it is applied by itself.
  let eligible = rows.map((_, i) => i).filter((i) => !departure || seasonContains(rows[i]!, departure));
  if (!eligible.length) eligible = rows.map((_, i) => i);
  const seasonDim = dims.find((dim) => dim.kind === "season");
  let autoSeason = false;
  if (seasonDim && departure) {
    const dated = eligible.filter((i) => inWindows(seasonWindows(parts[i]![seasonDim.index] ?? ""), departure));
    if (dated.length) { eligible = dated; autoSeason = new Set(dated.map((i) => parts[i]![seasonDim.index])).size === 1; }
  }

  // Narrow by the picks, the last one first; a pick with no row alongside the others is dropped.
  let candidates = eligible;
  const order = dims.map((dim) => dim.index).sort((a, b) => (a === lastPicked ? -1 : b === lastPicked ? 1 : a - b));
  for (const index of order) {
    const pick = picks[index];
    if (pick === undefined) continue;
    const narrowed = candidates.filter((i) => parts[i]![index] === pick);
    if (narrowed.length) candidates = narrowed;
  }
  const chosen = candidates[0]!;

  const choices = dims.map((dim): PlanChoice => {
    const values = [...new Set(eligible.map((i) => parts[i]![dim.index]).filter((value): value is string => Boolean(value)))];
    return {
      index: dim.index, kind: dim.kind, label: LABELS[dim.kind],
      options: values.map((value) => ({ value, label: dim.kind === "package" ? packageLabel(value) : value })),
      value: parts[chosen]![dim.index] ?? values[0] ?? "",
      auto: dim.kind === "season" && autoSeason
    };
  }).sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || a.index - b.index);

  const season = rows[chosen]!;
  const seasonChoice = choices.find((choice) => choice.kind === "season");
  const pkg = choices.find((choice) => choice.kind === "package");
  let seasonHint: string | null = null;
  if (seasonChoice?.auto) seasonHint = `${sentence(seasonChoice.value.replace(/\s*\(.*$/, ""))} · se aplica automáticamente`;
  else if (season.valid_from && season.valid_to && season.valid_from !== season.valid_to && !(season.departure_dates ?? []).length) {
    seasonHint = `Tarifa vigente ${formatCalendarDate(season.valid_from)} – ${formatCalendarDate(season.valid_to)}`;
  }
  return {
    choices: choices.filter((choice) => choice.options.length > 1 || choice.auto),
    category: season.service_category || null,
    season,
    seasonHint,
    packageLabel: pkg ? packageLabel(pkg.value).toLowerCase() : null
  };
}

/** The room types a season row has a price for, double first. */
export function pricedOccupancies(season: TripSeason | null, choices: Occupancy[] = ["double", "triple", "single"]): Occupancy[] {
  if (!season) return choices;
  const priced = choices.filter((occupancy) => season.rates.some((rate) => rate.occupancy === occupancy && rate.amount !== null));
  return priced.length ? priced : choices;
}
